import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { LoadingSpinner } from "../Components/LoadingSpinner";
import { client } from "../Clients";
import { bypassAuth } from "./auth0Config";
import { AccessFailure, apiHost, classifyAccessFailure, describeFailure } from "./accessFailure";

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");

type AccessGateProps = {
  audience?: string;
  children: React.ReactNode;
};

// Auth0 error codes that can only be repaired by an interactive login (silent token renewal is
// pointless). The same strings are also matched inside error MESSAGES because some failures : e.g.
// a rotated/expired refresh token : surface only in error_description, with a generic code.
const REAUTH_ERRORS = [
  "login_required",
  "consent_required",
  "interaction_required",
  "invalid_grant",
  "missing_refresh_token"
];


/**
 * Gate around the authenticated app. Two checks before anything renders:
 *   1. the Auth0 session is actually alive (a silent token acquisition), and
 *   2. the user is a MEMBER : GET /api/me returns 403 for any authenticated-but-unprovisioned
 *      principal (e.g. a random Google login), which the membership-gated server policy enforces.
 * Un 403 : et LUI SEUL : affiche le mur « Accès refusé » : le serveur a reconnu le porteur et
 * tranché. Toute autre erreur (aucune réponse, 404 de Traefik pendant un redéploiement, 5xx, CORS,
 * délai dépassé) affiche « Portail injoignable » avec un bouton Réessayer, parce qu'un poste
 * derrière un pare-feu d'entreprise ne doit jamais croire que son compte a été révoqué.
 * For members it fires the one-time POST /api/account/sync so the server has an up-to-date
 * display name for RBAC/presence.
 */
export function AccessGate({ audience, children }: AccessGateProps): React.ReactElement {
  const { isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect, error, logout, user } = useAuth0();
  // Early return before the hooks below is safe only because bypassAuth is a build-time constant:
  // it can never flip within a running app, so the hook order is identical on every render.
  if (bypassAuth) {
    return <>{children}</>;
  }
  const location = useLocation();
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [failure, setFailure] = useState<AccessFailure | null>(null);
  const [detail, setDetail] = useState<string>("");
  // Incrémenté par le bouton « Réessayer » : relance l'effet sans recharger la page, ce qui suffit
  // dès que le réseau revient (fin d'un redéploiement, VPN rebranché).
  const [attempt, setAttempt] = useState(0);
  const reauthStarted = useRef(false);
  const syncedRef = useRef(false);

  const returnTo = useMemo(
    () => `${location.pathname}${location.search}${location.hash}`,
    [location.pathname, location.search, location.hash]
  );

  useEffect(() => {
    let isMounted = true;
    if (!isAuthenticated) {
      setHasAccess(null);
      return;
    }

    (async () => {
      try {
        // Acquire a token to confirm the session is live (and trigger re-auth on expiry).
        const token = await getAccessTokenSilently(audience ? { authorizationParams: { audience } } : undefined);
        // MEMBERSHIP CHECK: the server admits only provisioned users / the root email. A random
        // authenticated login (e.g. Google) that isn't on the allowlist gets 403 here → denied.
        try {
          await axios.get(`${apiBase}/api/me`, { headers: { Authorization: `Bearer ${token}` } });
          if (isMounted) {
            setHasAccess(true);
            setFailure(null);
          }
        } catch (meErr: any) {
          // Un 401 n'est ni un refus ni une panne réseau : le jeton est périmé. On relance une
          // authentification interactive plutôt que d'accuser le compte ou le réseau.
          if (axios.isAxiosError(meErr) && meErr.response?.status === 401 && !reauthStarted.current) {
            reauthStarted.current = true;
            setHasAccess(null);
            loginWithRedirect({ appState: { returnTo }, authorizationParams: { prompt: "login" } });
            return;
          }
          if (isMounted) {
            setFailure(classifyAccessFailure(meErr));
            setDetail(describeFailure(meErr));
            setHasAccess(false);
          }
          return;
        }
      } catch (err: any) {
        const errCode = err?.error ?? err?.code;
        const errMessage = String(err?.error_description ?? err?.message ?? "").toLowerCase();
        const requiresLogin =
          REAUTH_ERRORS.includes(String(errCode)) ||
          [...REAUTH_ERRORS, "expired"].some((needle) => errMessage.includes(needle));

        if (requiresLogin && !reauthStarted.current) {
          // The ref (not state) guards against firing a second redirect while the first one is
          // navigating away : the effect can re-run in that window.
          reauthStarted.current = true;
          setHasAccess(null);
          loginWithRedirect({
            appState: { returnTo },
            authorizationParams: { prompt: "login" }
          });
          return;
        }

        if (isMounted) {
          // Échec d'obtention du jeton hors cas de reconnexion : Auth0 lui-même n'a pas été joint
          // (domaine bloqué, coupure). Jamais un refus de compte.
          setFailure("unreachable");
          setDetail(describeFailure(err));
          setHasAccess(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, getAccessTokenSilently, audience, loginWithRedirect, returnTo, attempt]);

  // Sync the user profile to the backend once per app load, after access is confirmed. Best-effort:
  // a failed sync must never lock the operator out.
  useEffect(() => {
    if (hasAccess && user && !syncedRef.current) {
      syncedRef.current = true;
      const email = user.email;
      const displayName = user.name || user.nickname || user.email;
      if (email) {
        client.account.apiAccountSyncPost({ email, displayName }).catch(() => {
          // Ignore sync errors : user can still use the app
        });
      }
    }
  }, [hasAccess, user]);

  if (isLoading || (isAuthenticated && hasAccess === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Redirect
        to={{
          pathname: "/",
          state: { returnTo },
        }}
      />
    );
  }

  if (!hasAccess) {
    const unreachable = failure === "unreachable";
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-xl">
          <div className="text-xs uppercase tracking-[0.3em] text-slate-500">BCC Admin</div>
          {unreachable ? (
            <>
              <h1 className="mt-2 text-2xl font-semibold text-slate-800">Portail injoignable</h1>
              <p className="mt-3 text-sm text-slate-500">
                Votre poste n'a pas réussi à joindre le serveur d'administration. Ce n'est pas un
                problème de compte ni d'autorisation : votre connexion (réseau d'entreprise,
                pare-feu, VPN, proxy ou coupure) bloque l'accès à{" "}
                <span className="font-semibold text-slate-700">{apiHost(apiBase)}</span>.
              </p>
              <p className="mt-3 text-sm text-slate-500">
                Essayez un autre réseau, par exemple un partage de connexion mobile. Si le problème
                persiste, transmettez le détail ci-dessous au service informatique.
              </p>
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Détail technique : {detail || "aucune réponse du serveur"}
              </div>
            </>
          ) : (
            <>
              <h1 className="mt-2 text-2xl font-semibold text-slate-800">Accès refusé</h1>
              <p className="mt-3 text-sm text-slate-500">
                Le serveur a bien reçu votre connexion, mais votre compte n'est pas autorisé à ouvrir
                la console d'administration. Contactez un administrateur pour qu'il vous attribue un
                rôle.
              </p>
            </>
          )}
          {error ? (
            <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error.message}
            </div>
          ) : null}
          {unreachable ? (
            <button
              type="button"
              className="mt-6 w-full rounded-xl bg-slate-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-700"
              onClick={() => {
                setHasAccess(null);
                setFailure(null);
                setAttempt((n) => n + 1);
              }}
            >
              Réessayer
            </button>
          ) : null}
          <button
            type="button"
            className="mt-3 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100"
            onClick={() =>
              logout({
                logoutParams: { returnTo: window.location.origin },
              })
            }
          >
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
